'use client';

import { ArrowRight } from 'lucide-react';

import { CrossfadeVideo } from '@/components/crossfade-video';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

import { BentoCardsSlider, type BentoCard } from './bento-grid';
import { CHALLENGE_AR, GUARANTEE_CARDS_AR } from './challenge-video.ar';

export const GUARANTEE_CARDS: BentoCard[] = [
  {
    id: 'apply',
    title: 'Apply once. Done.',
    description:
      "Submit a 30-second application. The moment your subscription goes live, you're protected. No metrics negotiation, no contractual back-and-forth.",
    modal: {
      eyebrow: 'Step 1 · Apply',
      headline: 'A 30-second application',
      paragraphs: [
        "We don't make you negotiate metrics or sign anything thick. Tell us a bit about your support setup, we confirm enrollment by email, and you're in.",
        "From the day your paid Open subscription goes live, the 120-day refund clock starts, no kickoff workshop, no goal-setting committee, no fine print.",
      ],
      bullets: [
        '30-second qualification form',
        "Confirmed enrollment within two business days",
        'Protection starts day one of your paid subscription',
        'No metrics or targets to negotiate',
      ],
    },
  },
  {
    id: 'run-in-prod',
    title: 'Run Open in production',
    description:
      "Use Open on real customer traffic for up to 120 days. No metrics to hit, no targets to chase, just run it on the work that matters.",
    modal: {
      eyebrow: 'Step 2 · Run it',
      headline: '120 days. Real traffic. No targets.',
      paragraphs: [
        "No sandbox demos, no cherry-picked tickets. Open handles your actual customer conversations across every channel you turn on, voice, chat, email, WhatsApp, social.",
        "And there's no automation rate or CSAT bar you have to clear. The guarantee is purely satisfaction-based: if it isn't working for your team, the refund is yours.",
      ],
      bullets: [
        'Full production traffic across every connected channel',
        'Per-conversation audit log with reasoning traces',
        'No automation, CSAT, or deflection target to hit',
        'Pull the plug any time, no lock-in',
      ],
    },
  },
  {
    id: 'we-refund',
    title: 'Refund, no questions',
    description:
      "Not 100% satisfied within 120 days? Tell us once and we wire every dollar of Open spend back, up to $2 million.",
    modal: {
      eyebrow: 'Step 3 · The guarantee',
      headline: 'Tell us once. Wired in 30 days.',
      paragraphs: [
        "You don't have to justify it. You don't have to hit a number. If you're not 100% satisfied within your first 120 days, tell us once and the refund hits your bank account.",
        'No clawbacks. No fine print. No "well actually that\'s not what we meant." The terms are short, plain English, and signed by both teams up front.',
      ],
      bullets: [
        'Every dollar of Open spend refunded, up to $2 million',
        'Wired within 30 days of your refund request',
        'You keep every workflow, playbook, and instruction we built',
        'No legal back-and-forth, terms are signed up front',
      ],
    },
  },
  {
    id: 'no-lock-in',
    title: 'No lock-in, ever',
    description:
      "Walk away any time. Your data, workflows, knowledge, and instructions are yours, exportable in open formats you can actually use elsewhere.",
    modal: {
      eyebrow: 'Lock-in · None',
      headline: 'Your data, your call, always',
      paragraphs: [
        'Most AI vendors quietly count on switching costs to keep you. We don\'t. Every workflow, instruction, knowledge source, and conversation log lives in your account and is exportable in open formats at any time.',
        'Cancel mid-contract and we\'ll spend the offboarding session helping your team move to whatever comes next.',
      ],
      bullets: [
        'Open formats for all data exports (JSON, CSV, Markdown)',
        'Knowledge base, workflows, and instructions are portable',
        'Conversation logs exported with full reasoning traces',
        'No "data hostage" clauses anywhere in the contract',
      ],
    },
  },
  {
    id: 'real-team',
    title: 'A real team behind it',
    description:
      "Not a Discord channel. You get named solutions engineers, AI trainers, and a project lead who own your deployment end-to-end.",
    modal: {
      eyebrow: 'Humans · Real ones',
      headline: 'Named humans, not a queue',
      paragraphs: [
        "Self-serve is great for trying things out. It is not great when a real customer-facing issue is on the line. From kickoff to ongoing operations, you get named people on Open's side who own your account.",
        'They join your standups, sit in on your QBRs, and ship workflow changes alongside your team, usually inside one day.',
      ],
      bullets: [
        'Named solutions engineer and AI trainer per account',
        'Same-day turnaround on workflow and instruction changes',
        'Quarterly business reviews with leadership',
        'Direct Slack/Teams channel with your account team',
      ],
    },
  },
  {
    id: 'security',
    title: 'Security on day one',
    description:
      "SOC 2 Type II and ISO 27001 certified, GDPR-aligned, HIPAA- and PCI-ready. Your data is encrypted, isolated to your tenant, and never trains shared models.",
    modal: {
      eyebrow: 'Security · By default',
      headline: 'Enterprise-grade from the first ticket',
      paragraphs: [
        "Compliance isn't a roadmap item, it ships in the box. Open is fully certified: SOC 2 Type II and ISO 27001 certificates in hand, GDPR-aligned, and configurable for HIPAA and PCI workflows out of the box.",
        'Your data is encrypted in transit and at rest, isolated to your tenant, and never used to train shared foundation models.',
      ],
      bullets: [
        'SOC 2 Type II certified, audited annually',
        'ISO 27001 certified',
        'GDPR-aligned, HIPAA- and PCI-ready configurations',
        'Tenant isolation; your data never trains shared models',
        'Optional self-hosted deployment for regulated industries',
      ],
    },
  },
];

const VIDEO_MASK =
  'radial-gradient(ellipse closest-side at center, #000 35%, rgba(0,0,0,0.5) 70%, transparent 100%)';

/**
 * Full-bleed black section with a centered video that:
 *  - starts the first time the section scrolls into view,
 *  - loops seamlessly via a crossfade between two stacked <video>s,
 *  - has a circular mask so the clip blends into the black background,
 *  - is completely non-interactive (no controls, no click to restart).
 */
export const ChallengeVideo = () => {
  const { locale } = useTranslation();
  const isAr = locale === 'ar';
  const copy = isAr
    ? CHALLENGE_AR
    : {
        heading: 'The 2 million dollar guarantee.',
        subtitle: [
          "You're protected for your entire first 120 days. Not 100% satisfied? We refund every dollar of Open spend, up to $2 million back.",
        ],
        cta: 'Learn more about the guarantee',
      };
  const cards = isAr ? GUARANTEE_CARDS_AR : GUARANTEE_CARDS;

  return (
    <section
      data-nav-theme="dark"
      className="bg-black py-16 md:py-32"
    >
      <div className="container flex flex-col items-center">
        <div className="mb-12 flex max-w-3xl flex-col items-center text-center md:mb-16">
          <h2 className="font-serif text-balance text-white text-3xl md:text-5xl lg:text-5xl">
            {copy.heading}
          </h2>
          <div className="mt-6 flex max-w-xl flex-col gap-4 text-base leading-relaxed text-neutral-400 md:text-lg">
            {copy.subtitle.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
          <Link
            href="/guarantee"
            prefetch
            className="group/cta mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-white transition-colors hover:text-amber-400"
          >
            {copy.cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/cta:translate-x-0.5 rtl:rotate-180 rtl:group-hover/cta:-translate-x-0.5" />
          </Link>
        </div>

        <CrossfadeVideo
          src="/home/features/challange.mp4"
          maskImage={VIDEO_MASK}
          className="max-w-5xl"
        />
      </div>

      {/* Detail cards, full-bleed slider so cards bleed off BOTH sides
       *  while the first card still aligns with the container's left edge.
       *  (The slider handles its own first-card offset internally.) */}
      <div className="mt-16 md:mt-24">
        <BentoCardsSlider
          cards={cards}
          theme="dark"
          // Pull the arrows back to the container's edge (right in LTR, left
          // in RTL, since `justify-end` on the controls row already flips
          // logically) so they don't sit at the bleeding edge of the viewport.
          controlsClassName="ltr:pr-[max(var(--header-padding-x),calc((100%-1152px)/2+var(--header-padding-x)))] rtl:pl-[max(var(--header-padding-x),calc((100%-1152px)/2+var(--header-padding-x)))]"
        />
      </div>
    </section>
  );
};

export default ChallengeVideo;
